/**
 * The table before the cards: the same felt and the same ring, with the seats
 * written on it and the code where the deck will be. Sitting down, moving seat,
 * adding a bot, starting and leaving are the only things that can happen here.
 *
 * Moving matters because seats are teams: partners sit opposite. Once everyone
 * has arrived the only free seats are usually the bots', so a seated player can
 * take one of those too — the bot takes the seat they came from.
 */
import { useState } from 'react'
import { botLevelOf, type BotLevel, type SeatOccupant } from '@cards/shared'
import { BotIcon, countWord, Menu } from '../table/atoms'
import { useSettings } from '../settings/settings'
import { Placemat, SeatRing, TableDeck } from './SeatRing'
import type { TableView } from '../table/view'
import type { TableSend } from '../socket/useTable'

export function Lobby({
  view,
  send,
  onMenu,
  onShare,
  onLeave,
}: {
  view: TableView
  send: TableSend
  onMenu: () => void
  onShare: () => void
  onLeave: () => void
}): JSX.Element {
  const room = view.snapshot.room
  const isHost = room.hostUserId === view.snapshot.you.userId
  const [settings] = useSettings()
  const host = room.seats.find((o) => o.kind === 'human' && o.userId === room.hostUserId)
  const hostName = host && host.kind === 'human' ? host.name : 'the host'
  const seated = view.mySeat !== null
  const humans = room.seats.filter((s) => s.kind === 'human').length
  const filled = room.seats.filter((s) => s.kind !== 'empty').length
  const ready = filled === room.seats.length
  // The last player out closes the table rather than leaving it to bots, so
  // the button says which of the two it is going to do.
  const alone = seated && humans === 1
  // One level for every bot at the table: the bots already seated say what it
  // is, and a new one joins at it. Before any bot sits it is the host's pick.
  const bots = room.seats.flatMap((o) => (o.kind === 'bot' && o.replacedUserId === null ? [o] : []))
  const [picked, setPicked] = useState<BotLevel>('medium')
  const level: BotLevel = bots.length > 0 && bots.every((b) => botLevelOf(b.strategyId) === 'hard')
    ? 'hard'
    : bots.length > 0 ? 'medium' : picked
  const setLevel = (next: BotLevel) => {
    setPicked(next)
    room.seats.forEach((o, seat) => {
      if (o.kind === 'bot' && o.replacedUserId === null && botLevelOf(o.strategyId) !== next) send.addBot(seat, next)
    })
  }

  return (
    <div className="app lobby-app">
      <header className="status">
        <div className="reading">
          <div className="num index">{view.gameUi.name}</div>
          <div className="cap">
            {humans} {humans === 1 ? 'player' : 'players'} here
          </div>
        </div>
        <div className="reading" style={{ alignItems: 'center' }} />
        <div className="score" style={{ marginRight: 30 }} />
      </header>
      <Menu onClick={onMenu} />

      <section className="felt lobby-felt">
        <SeatRing
          seats={room.seats.flatMap((occupant, seat) => {
            const s = view.seats[seat]
            if (!s) return []
            return [{
              key: seat,
              position: s.position,
              node: (
                <LobbySeat
                  occupant={occupant}
                  isMe={s.isMe}
                  isHostSeat={occupant.kind === 'human' && occupant.userId === room.hostUserId}
                  seated={seated}
                  isHost={isHost}
                  onSit={() => send.chooseSeat(seat)}
                  onAddBot={() => send.addBot(seat, level)}
                  onRemove={() => send.removeBot(seat)}
                />
              ),
            }]
          })}
          centre={<TableDeck code={room.code} deck={settings.deck} onShare={onShare} />}
        />
      </section>

      <section className="hand-band" style={{ height: 'auto', minHeight: 144, justifyContent: 'flex-end', gap: 8 }}>
        <div className="lobby-options">
          <div className="option-line">
            <span className="muted">Bots</span>
            <div className="chips" role="radiogroup" aria-label="Bot level">
              {(['medium', 'hard'] as const).map((l) => (
                <button
                  key={l}
                  type="button"
                  role="radio"
                  aria-checked={level === l}
                  className={level === l ? 'chip on' : 'chip'}
                  disabled={!isHost}
                  onClick={() => level !== l && setLevel(l)}
                >
                  {l === 'hard' ? 'Hard' : 'Medium'}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="lobby-options">
          <div className="option-line">
            <span className="muted">Coach</span>
            <div className="chips" role="radiogroup" aria-label="Coach mode for every player">
              {([false, true] as const).map((on) => {
                const checked = (room.coachAllowed === true) === on
                return (
                  <button
                    key={String(on)}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    className={checked ? 'chip on' : 'chip'}
                    disabled={!isHost}
                    onClick={() => !checked && send.setCoach(on)}
                  >
                    {on ? 'On for everyone' : 'Off'}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
        <div className="lobby-options">
          <div className="option-line">
            <span className="muted">Who joins</span>
            <div className="chips" role="radiogroup" aria-label="Who can join this table">
              {([false, true] as const).map((on) => {
                const checked = (room.listed === true) === on
                return (
                  <button
                    key={String(on)}
                    type="button"
                    role="radio"
                    aria-checked={checked}
                    className={checked ? 'chip on' : 'chip'}
                    disabled={!isHost}
                    onClick={() => !checked && send.setListed(on)}
                  >
                    {on ? 'Anyone' : 'Invite only'}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
        <Options view={view} isHost={isHost} send={send} />
        <div className="hint" style={{ justifyContent: 'center' }}>
          <div className="hint-text muted">
            {ready
              ? isHost ? 'Everyone is seated.' : `Everyone is seated. ${hostName} deals first.`
              : `${capitalise(countWord(room.seats.length - filled))} ${room.seats.length - filled === 1 ? 'seat is' : 'seats are'} empty. Invite friends or add bots.`}
          </div>
        </div>
        {/* One slot, one brass button: while seats are empty the job is to fill
            them, so the slot shares the table; once they are full it deals.
            The slot never changes height, so nothing on the felt moves. */}
        {ready ? (
          <button className="btn btn-primary btn-block" disabled={!isHost} onClick={() => send.startMatch()}>
            {isHost ? 'Deal the first hand' : `Waiting for ${hostName} to deal`}
          </button>
        ) : (
          <button className="btn btn-primary btn-block" onClick={onShare}>
            Share the table
          </button>
        )}
        <button className="btn btn-quiet btn-block" onClick={onLeave}>
          {alone ? 'Close the table' : 'Leave the table'}
        </button>
      </section>
    </div>
  )
}

/**
 * The game's rules options, one row each. The host changes them; everyone
 * else sees what was chosen. The room's config is the whole validated config,
 * so a change is the rest of it plus the new value — except an option that
 * decides the others (Belote's variant), which is sent alone so the rest
 * falls back to what it implies.
 */
function Options({ view, isHost, send }: { view: TableView; isHost: boolean; send: TableSend }): JSX.Element | null {
  const options = view.gameUi.options ?? []
  if (options.length === 0) return null
  const config = (view.snapshot.room.config ?? {}) as Record<string, unknown>
  const current = (key: string, first: unknown) => (config[key] === undefined ? first : config[key])
  return (
    <div className="lobby-options">
      {options.map((option) => (
        <div className="option-line" key={option.key}>
          <span className="muted">{option.label}</span>
          <div className="chips" role="radiogroup" aria-label={option.label}>
            {option.choices.map((choice) => {
              const on = current(option.key, option.choices[0].value) === choice.value
              return (
                <button
                  key={String(choice.value)}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  className={on ? 'chip on' : 'chip'}
                  disabled={!isHost}
                  onClick={() => {
                    if (on) return
                    send.updateConfig(option.resetsOthers ? { [option.key]: choice.value } : { ...config, [option.key]: choice.value })
                  }}
                >
                  {choice.label}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

const capitalise = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)

/**
 * One seat in the lobby. A person is their name, with `host` under the one who
 * deals; a bot is its name and level, with the host's two things to do with
 * it; an empty seat is a place at the table you can tap to sit in.
 */
function LobbySeat({
  occupant,
  isMe,
  isHostSeat,
  seated,
  isHost,
  onSit,
  onAddBot,
  onRemove,
}: {
  occupant: SeatOccupant
  isMe: boolean
  isHostSeat: boolean
  seated: boolean
  isHost: boolean
  onSit: () => void
  onAddBot: () => void
  onRemove: () => void
}): JSX.Element {
  if (occupant.kind === 'empty') {
    return (
      <div className="lobby-seat">
        <Placemat label={seated ? 'Move here' : 'Sit here'} onClick={onSit} lit={!seated} />
        {isHost ? (
          <button type="button" className="btn btn-quiet seat-btn" onClick={onAddBot}>
            Add a bot
          </button>
        ) : null}
      </div>
    )
  }
  if (occupant.kind === 'bot') {
    return (
      <div className="lobby-seat">
        <div className="seat-name">
          <BotIcon />
          {occupant.name}
        </div>
        <div className="seat-sub">{botLevelOf(occupant.strategyId) === 'hard' ? 'hard bot' : 'bot'}</div>
        {/* A bot holding a seat for a player who stepped out is not a free
            seat, so it is not offered as one. */}
        {seated && occupant.replacedUserId === null ? (
          <button type="button" className="btn btn-quiet seat-btn" onClick={onSit}>
            Swap here
          </button>
        ) : null}
        {isHost ? (
          <button type="button" className="btn btn-quiet seat-btn" onClick={onRemove}>
            Remove
          </button>
        ) : null}
      </div>
    )
  }
  const sub = [isHostSeat ? 'host' : null, !isMe && !occupant.connected ? 'away' : null].filter(Boolean).join(', ')
  return (
    <div className="lobby-seat">
      <div className="seat-name">{isMe ? 'You' : occupant.name}</div>
      <div className="seat-sub">{sub || '\u00A0'}</div>
    </div>
  )
}
